import last from "lodash/last";
import { type FunctionComponent, useCallback, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { tracking } from "shared/data";
import { useResponsive, useTrackImpressionEventByIntersection } from "shared/hooks";
import { Edition, useEditionValue } from "shared/lib/edition";
import { theme } from "shared/lib/styles";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type {
  aroundHomeV2Query,
  aroundHomeV2Query$data,
} from "scmp-app/queries/__generated__/aroundHomeV2Query.graphql";

import { HomeV2AroundItem } from "./around-item";
import {
  Container,
  GridContainer,
  GridItem,
  StyledArrowNext,
  StyledArrowPrevious,
  StyledSwiper,
  StyledSwiperSlide,
  SwiperContainer,
  SwiperWrapper,
  Title,
} from "./styles";

type Props = {
  className?: string;
  existingEntityIds: string[];
};

export const HomeV2Around: FunctionComponent<Props> = ({ className, existingEntityIds }) => {
  // As this query is too large, better separate it out to prevent one slow query
  const queryReference = useLazyLoadQuery<aroundHomeV2Query>(
    graphql`
      query aroundHomeV2Query($excludeEntityIds: [String]) {
        # Hong Kong
        section_top_2: queue(filter: { name: "section_top_2" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...aroundItemHomeV2QueueItemsEdge
            }
          }
        }
        # Asia
        section_top_3: queue(filter: { name: "section_top_3" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...aroundItemHomeV2QueueItemsEdge
            }
          }
        }
        # World
        section_top_5: queue(filter: { name: "section_top_5" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...aroundItemHomeV2QueueItemsEdge
            }
          }
        }
        # Business
        section_top_92: queue(filter: { name: "section_top_92" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...aroundItemHomeV2QueueItemsEdge
            }
          }
        }
        # Economy
        section_top_328461: queue(filter: { name: "section_top_328461" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...aroundItemHomeV2QueueItemsEdge
            }
          }
        }
        # Tech
        section_top_36: queue(filter: { name: "section_top_36" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...aroundItemHomeV2QueueItemsEdge
            }
          }
        }
        # Comment
        section_top_93: queue(filter: { name: "section_top_93" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...aroundItemHomeV2QueueItemsEdge
            }
          }
        }
        # Sport
        section_top_95: queue(filter: { name: "section_top_95" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...aroundItemHomeV2QueueItemsEdge
            }
          }
        }
        # Postmag
        section_top_71: queue(filter: { name: "section_top_71" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...aroundItemHomeV2QueueItemsEdge
            }
          }
        }
        # TWIA
        section_top_323045: queue(filter: { name: "section_top_323045" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...aroundItemHomeV2QueueItemsEdge
            }
          }
        }
        # Video Top
        video_homepage_top: queue(filter: { name: "video_homepage_top" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...aroundItemHomeV2QueueItemsEdge
            }
          }
        }
        # SCMP Cooking
        latest_recipe: queue(filter: { name: "latest_recipe" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...aroundItemHomeV2QueueItemsEdge
            }
          }
        }
        # KPOP
        section_top_507629: queue(filter: { name: "section_top_507629" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...aroundItemHomeV2QueueItemsEdge
            }
          }
        }
        # MorningStudio
        homepage_morning_studio_int: queue(filter: { name: "homepage_morning_studio_int" }) {
          items(first: 4, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...aroundItemHomeV2QueueItemsEdge
            }
          }
        }
      }
    `,
    {
      excludeEntityIds: existingEntityIds,
    },
    {
      fetchPolicy: "store-or-network",
    },
  );

  const listMapping = useMemo<
    {
      name: string;
      numberOfItemsDisplay?: number;
      queueName: keyof NonNullable<aroundHomeV2Query$data>;
      urlAlias: string;
      withSCMPLabLogo?: boolean;
    }[]
  >(
    () => [
      {
        name: "HONG KONG",
        queueName: "section_top_2",
        urlAlias: "/news/hong-kong",
      },
      {
        name: "ASIA",
        queueName: "section_top_3",
        urlAlias: "/news/asia",
      },
      {
        name: "WORLD",
        queueName: "section_top_5",
        urlAlias: "/news/world",
      },
      {
        name: "BUSINESS",
        queueName: "section_top_92",
        urlAlias: "/business",
      },
      {
        name: "ECONOMY",
        queueName: "section_top_328461",
        urlAlias: "/economy",
      },
      {
        name: "TECH",
        queueName: "section_top_36",
        urlAlias: "/tech",
      },
      {
        name: "COMMENT",
        queueName: "section_top_93",
        urlAlias: "/comment",
      },
      {
        name: "SPORT",
        queueName: "section_top_95",
        urlAlias: "/sport",
      },
      {
        name: "POSTMAG",
        queueName: "section_top_71",
        urlAlias: "/postmag",
      },
      {
        name: "THIS WEEK IN ASIA",
        queueName: "section_top_323045",
        urlAlias: "/week-asia",
      },
      {
        name: "VIDEO",
        queueName: "video_homepage_top",
        urlAlias: "/video",
      },
      {
        name: "SCMP COOKING",
        queueName: "latest_recipe",
        urlAlias: "/cooking",
      },
      {
        name: "K-POP",
        queueName: "section_top_507629",
        urlAlias: "/k-pop",
      },
      {
        name: "MORNING STUDIO",
        numberOfItemsDisplay: 2,
        queueName: "homepage_morning_studio_int",
        urlAlias: "https://morningstudio.scmp.com/",
      },
    ],
    [],
  );

  const edition = useEditionValue();

  const isTabletUp = useResponsive(theme.breakpoints.up("tablet"), true);

  const newList = useMemo(
    () =>
      listMapping.reduce(
        (accumulator, item) => {
          if (edition === Edition.Asia && item.name === "THIS WEEK IN ASIA") return accumulator;
          else if (edition !== Edition.Asia && item.name === "HONG KONG") return accumulator;
          const excludeEntityIds = last(accumulator)?.excludeEntityIds ?? [];
          const limit = item.numberOfItemsDisplay ?? 3;
          const edges = queryReference[item.queueName]?.items?.edges
            ?.filter(edge => !excludeEntityIds.includes(edge.node.entityId ?? ""))
            .slice(0, limit);
          if (!edges) return accumulator;
          accumulator.push({
            excludeEntityIds: [...excludeEntityIds, ...edges.map(edge => edge.node.entityId ?? "")],
            items: edges,
            name: item.name,
            urlAlias: item.urlAlias,
            withSCMPLabLogo: item.withSCMPLabLogo,
          });
          return accumulator;
        },
        [] as {
          excludeEntityIds: string[];
          items: NonNullable<
            NonNullable<aroundHomeV2Query$data[keyof aroundHomeV2Query$data]>["items"]
          >["edges"];
          name: string;
          urlAlias: string;
          withSCMPLabLogo?: boolean;
        }[],
      ),
    [queryReference, edition, listMapping],
  );

  const renderSwiper = () => (
    <SwiperContainer>
      <SwiperWrapper>
        <StyledSwiper
          breakpoints={{
            0: {
              spaceBetween: 32,
            },
          }}
          freeMode={true}
          modules={[Navigation]}
          navigation={{
            disabledClass: "around-swiper-button-disabled",
            nextEl: ".around-swiper-button-next",
            prevEl: ".around-swiper-button-prev",
          }}
          pagination={false}
          slidesOffsetAfter={isTabletUp ? 0 : 20}
          slidesOffsetBefore={isTabletUp ? 0 : 20}
          slidesPerGroupAuto={true}
          slidesPerView="auto"
        >
          {newList.map(listItem => (
            <StyledSwiperSlide key={listItem.name}>
              <HomeV2AroundItem
                reference={listItem.items}
                title={listItem.name}
                urlAlias={listItem.urlAlias}
                withSCMPLabLogo={listItem.withSCMPLabLogo}
              />
            </StyledSwiperSlide>
          ))}
        </StyledSwiper>
        <div className="swiper-button around-swiper-button-prev">
          <StyledArrowPrevious />
        </div>
        <div className="swiper-button around-swiper-button-next">
          <StyledArrowNext />
        </div>
      </SwiperWrapper>
    </SwiperContainer>
  );

  const renderGrid = () => (
    <GridContainer>
      {newList.map(listItem => (
        <GridItem key={listItem.name}>
          <HomeV2AroundItem
            reference={listItem.items}
            title={listItem.name}
            urlAlias={listItem.urlAlias}
            withSCMPLabLogo={listItem.withSCMPLabLogo}
          />
        </GridItem>
      ))}
    </GridContainer>
  );

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: newList.at(-1)?.excludeEntityIds.join(","),
        page_type: "homepage",
        widget_name: "around_scmp",
      },
      subcategory: "widget",
    }),
    [newList],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: tracking.module.AroundSCMP,
      }}
    >
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <Title>Around SCMP</Title>
        {renderGrid()}
        {renderSwiper()}
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeV2Around.displayName = "HomeV2Around";
